import rules from "../../rules";
import { getAccessSnapshot } from "../../context/AccessControl/accessSnapshot";

/**
 * Traducción de los permisos de UI al catálogo del backend (doc 0382 §2.6).
 *
 * `rules.js` usa un vocabulario orientado a componentes ("user-modal:editProfile")
 * y el catálogo del backend habla de módulos y acciones ("users:editar"). Este
 * mapa cubre los permisos de acción del panel; el vocabulario de navegación
 * ("drawer-service-items:view", "drawer-admin-items:view") NO está aquí a
 * propósito y se resuelve por la vía heredada.
 *
 * REGLA: una clave ausente de este mapa jamás se consulta contra el catálogo del
 * backend. Ver el WHY del bloque `checkSnapshot`.
 */
const UI_TO_PERMISSION = {
  "user-modal:editProfile": "users:editar",
  "user-modal:editQueues": "users:editar",
  "contacts-page:deleteContact": "contacts:eliminar",
  "ticket-options:deleteTicket": "tickets:eliminar",
  "connections-page:addConnection": "connections:crear",
  "connections-page:actionButtons": "connections:editar",
  "connections-page:editOrDeleteConnection": "connections:editar"
};

/** Comprobación heredada, contra el `rules.js` estático. */
const checkLegacy = (role, action, data) => {
  if (role === undefined || role === null || role === "") return false;

  const permissions = rules[role];
  if (!permissions) return false;

  const staticPermissions = permissions.static;
  if (staticPermissions && staticPermissions.includes(action)) return true;

  const dynamicPermissions = permissions.dynamic;
  if (dynamicPermissions) {
    const permissionCondition = dynamicPermissions[action];
    if (!permissionCondition) return false;
    return permissionCondition(data);
  }

  return false;
};

/**
 * Comprobación contra el catálogo RBAC del backend.
 *
 * WHY solo consulta claves presentes en `UI_TO_PERMISSION`: el catálogo del
 * backend contiene permisos de dominio ("users:editar"), no el vocabulario de
 * navegación del menú ("drawer-admin-items:view"). Preguntarle por una clave que
 * no conoce devuelve `false`, no "no sé" — y ese `false` se leía como denegación.
 *
 * Eso tumbó el menú entero: `AccordionMenu` envuelve CADA ítem en `<Can>`, así
 * que al denegarlos todos las cabeceras seguían dibujándose pero el `Collapse`
 * quedaba vacío. El síntoma parecía "el acordeón no abre"; en realidad abría
 * sobre una lista sin elementos.
 */
const checkSnapshot = perform => {
  const mapped = UI_TO_PERMISSION[perform];
  if (!mapped) return false;

  const snapshot = getAccessSnapshot();
  return snapshot.loaded && snapshot.can(mapped);
};

/**
 * `<Can role={...} perform="..." yes={...} no={...} />`
 *
 * WHY lee de un snapshot en módulo y NO de un contexto de React: `<Can>` se usa
 * en 37 sitios, entre ellos el menú lateral. Suscribirlo a un contexto lo obliga
 * a re-renderizar cada vez que cambia el estado de autenticación —que cambia a
 * menudo— y arrastra a sus padres, con el riesgo de perder el estado local de
 * componentes como los acordeones del menú. Un snapshot plano lo mantiene como
 * función pura: mismo comportamiento de siempre, sin acoplar el árbol.
 *
 * WHY la comprobación heredada va PRIMERA y el RBAC solo suma: mientras el flag
 * `rbacScopeEnabled` esté OFF y el catálogo no cubra todo el vocabulario de UI,
 * el RBAC únicamente puede CONCEDER, nunca REVOCAR. Así el panel se comporta
 * igual que antes en las 40 instancias. La revocación llegará cuando el catálogo
 * cubra la UI completa, y será un cambio deliberado, no el efecto colateral de
 * que falte una clave en un mapa.
 */
const Can = ({ role, perform, data, yes, no }) => {
  const allowed = checkLegacy(role, perform, data) || checkSnapshot(perform);

  return allowed ? yes() : no();
};

Can.defaultProps = {
  yes: () => null,
  no: () => null
};

export { Can };
